import Icon from '@material-ui/core/Icon';
import { ThemeProvider } from '@material-ui/styles';
import classNames from 'classnames';
import React, { useState } from 'react';
import { Redirect, Switch } from 'react-router';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { AuthorisationPermissions } from './AuthorisationPermissions';
import { AuthorizedRoute } from './Components/Auth/AuthorizedRoute';
import { Button, ButtonMode } from './Components/Button';
import { ErrorBoundary } from './Components/ErrorBoundary';
import { AddPatientIcon, DoubleArrowLeftIcon, UserAddIcon } from './Components/Icons';
import { NavMenu, NavMenuItemWithPermission } from './Components/Layout/NavMenu';
import { Toolbar, ToolbarDivider } from './Components/Layout/Toolbar';
import { SearchInput } from './Components/SearchInput';
import { TwoPanelLayout } from './Components/TwoPanelLayout';
import { GroupList } from './Groups/GroupList';
import {
    routeForAuthorisationDashboard,
    routeForGroups,
    routeForNewGroup,
    routeForNewUser,
    routeForUsers
} from './Routes';
import styles from './SettingsFeature.module.scss';
import { GlobalSnackbar } from './Shared/GlobalSnackbar';
import { body } from './Styles/Colours';
import { muiAuthorisationTheme } from './Styles/Theme';
import { UserList } from './Users/UserList';

export function SettingsFeature(): React.ReactElement {
    return (
        <ErrorBoundary>
            <ThemeProvider theme={muiAuthorisationTheme}>
                <GlobalSnackbar>
                    <Settings />
                </GlobalSnackbar>
            </ThemeProvider>
        </ErrorBoundary>
    );
}

function LeftMenu(props: { onNavigate(): void }) {
    const [menuCollapsed, setMenuCollapsed] = useState<boolean>(false);
    const location = useLocation();

    const sideBar = (
        <NavMenu isCollapsed={menuCollapsed}>
            <NavMenuItemWithPermission
                permission={AuthorisationPermissions.WriteUsersGroupsCode}
                onClick={props.onNavigate}
                icon={<Icon>person</Icon>}
                title={'Users'}
                pathname={location.pathname}
                location={routeForUsers}
                referrerLocation={location}
            />
            <NavMenuItemWithPermission
                permission={AuthorisationPermissions.WriteUsersGroupsCode}
                onClick={props.onNavigate}
                icon={<Icon>group</Icon>}
                title={'Groups'}
                pathname={location.pathname}
                location={routeForGroups}
                referrerLocation={location}
            />
        </NavMenu>
    );

    const sidebarClass = classNames(styles.sidebar, {
        [styles.sidebarCollapsed]: menuCollapsed
    });

    const sidebarToggleClass = classNames(styles.closeButton, {
        [styles.closeButtonCollapsed]: menuCollapsed
    });

    const sidebarToggleTitle = menuCollapsed ? 'Expand menu' : '';

    return (
        <div className={sidebarClass}>
            <div className={styles.sidebarMenu}>{sideBar}</div>
            <div className={styles.sidebarCollapseToggle}>
                <Button
                    testId={'sidebar-collapse'}
                    mode={ButtonMode.text}
                    className={sidebarToggleClass}
                    title={sidebarToggleTitle}
                    startIcon={<DoubleArrowLeftIcon className={styles.sidebarToggleIcon} color={body} size={'24'} />}
                    onClick={() => setMenuCollapsed(!menuCollapsed)}
                >
                    {!menuCollapsed && <div className={styles.sidebarToggleText}>Collapse menu</div>}
                </Button>
            </div>
        </div>
    );
}

const getToolbarItems = (): { searchMessage: string } => {
    const matchUsersRoute = useRouteMatch(routeForUsers);
    const matchGroupsRoute = useRouteMatch(routeForGroups);

    let message = 'Search all';

    if (matchUsersRoute) {
        message = 'Search users';
    } else if (matchGroupsRoute) {
        message = 'Search groups';
    }

    return { searchMessage: message };
};

function Settings() {
    const history = useHistory();
    const [searchTerm, setSearchTerm] = useState('');
    const { searchMessage } = getToolbarItems();

    const matchUsersRoute = useRouteMatch(routeForUsers);
    const matchGroupsRoute = useRouteMatch(routeForGroups);

    const openNewUserDialog = () => history.push(routeForNewUser);
    const newGroup = () => history.push(routeForNewGroup);

    const toolbarComponent = (
        <Toolbar
            leftContent={
                <>
                    <div className={styles.searchInput}>
                        <SearchInput
                            placeholder={searchMessage}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            onClear={() => setSearchTerm('')}
                        />
                    </div>
                    <ToolbarDivider />
                </>
            }
            rightContent={
                matchGroupsRoute ? (
                    <Button testId='create-group' onClick={newGroup} startIcon={<UserAddIcon color='white' />}>
                        Create group
                    </Button>
                ) : matchUsersRoute ? (
                    <Button
                        testId='create-user'
                        onClick={openNewUserDialog}
                        startIcon={<AddPatientIcon color='white' />}
                    >
                        Create user
                    </Button>
                ) : null
            }
        />
    );

    return (
        <div className={styles.container}>
            <div className={styles.toolbarHeader}>{toolbarComponent}</div>
            <TwoPanelLayout
                leftPanel={
                    <LeftMenu
                        onNavigate={() => {
                            setSearchTerm('');
                        }}
                    />
                }
                contents={
                    <Switch>
                        <Redirect from={routeForAuthorisationDashboard} to={routeForUsers} exact />
                        <AuthorizedRoute
                            path={routeForUsers}
                            permission={AuthorisationPermissions.WriteUsersGroupsCode}
                            render={() => <UserList searchTerm={searchTerm} />}
                        />
                        <AuthorizedRoute
                            path={routeForGroups}
                            permission={AuthorisationPermissions.WriteUsersGroupsCode}
                            render={() => <GroupList searchTerm={searchTerm} />}
                        />
                    </Switch>
                }
            />
        </div>
    );
}
